import React from "react";
import { LucideIcon } from "lucide-react";
import { SynapticText } from "@/components/hero/SynapticText";

interface Agent {
  icon: LucideIcon;
  title: string;
  description: string;
  clinicianBenefit: string;
  patientBenefit: string;
}

interface NeuralNodeDetailsProps {
  agent: Agent;
  isMobile: boolean;
  onClose: () => void;
}

export const NeuralNodeDetails: React.FC<NeuralNodeDetailsProps> = ({
  agent,
  isMobile,
  onClose
}) => {
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-background/98 backdrop-blur-md border border-primary/20 rounded-xl w-full max-w-lg min-w-[320px] max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <agent.icon className="w-6 h-6 text-primary" />
                <h3 className="font-bold text-xl text-white">{agent.title}</h3>
              </div>
              <button 
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary/20 transition-colors"
                aria-label="Close details"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-1">
              <p className="text-base text-white leading-relaxed whitespace-normal">
                {agent.description}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-white/80 rounded-full" />
                  <span className="text-base font-bold text-white">For Clinicians</span>
                </div>
                <div className="pl-4">
                  <p className="text-sm text-white/90 leading-relaxed whitespace-normal">
                    {agent.clinicianBenefit}
                  </p>
                </div>
              </div>
              
              <div className="border-t border-border/30 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  <span className="text-base font-bold text-white">For Patients</span>
                </div>
                <div className="pl-4">
                  <p className="text-sm text-white/90 leading-relaxed whitespace-normal">
                    {agent.patientBenefit}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="
        absolute top-full mt-8 left-1/2 transform -translate-x-1/2 
        bg-background/98 backdrop-blur-md border border-primary/20 rounded-xl p-4 
        shadow-2xl shadow-primary/10 z-50 w-64 animate-fade-in-scale
      "
    >
      <div className="space-y-3">
        <SynapticText 
          text={agent.description}
          className="text-sm text-card-foreground"
          delay={100}
        />
        
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0 animate-pulse" />
            <div>
              <span className="text-xs font-bold text-primary">Clinician:</span>
              <SynapticText 
                text={agent.clinicianBenefit}
                className="text-xs text-card-foreground/90 ml-1"
                delay={200}
              />
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-accent rounded-full mt-1.5 flex-shrink-0 animate-pulse" />
            <div>
              <span className="text-xs font-bold text-accent">Patient:</span>
              <SynapticText 
                text={agent.patientBenefit}
                className="text-xs text-card-foreground/90 ml-1"
                delay={300}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Close indicator */}
      <div className="text-center mt-3 text-xs text-muted-foreground">
        Click to close
      </div>
    </div>
  );
};